import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useEffect, useState } from "react";
import { cpfMask } from "utils/cpf";
import { IMovimentacaoResumed } from "../../../types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc)

interface IFilters {
    nomeCpf: string,
    plano: 'Todos' | 'PlanoA' | 'PlanoB',
    cargo: 'Todos' | 'Auxiliar' | 'Gerente'
    parentesco: 'Todos' | 'Titular' | 'Dependente',
}

function formatDate(dateToFormat: string) {
  try {
    if (!dayjs(dateToFormat).isValid()) throw new Error();
    
    return dayjs(dateToFormat).utc().format(`DD/MM/YYYY`);
  } catch (error) {
    return `-`
  }
}

export function MovimentacaoTable({ data: xxAllDataB }: { data: IMovimentacaoResumed['result'] }) {
  const [xxAllDataz, setXxAllData] = useState<IMovimentacaoResumed['result']>(xxAllDataB);

  const xxAllFilters: IFilters = {
      nomeCpf: '',
      plano: 'Todos',
      cargo: 'Todos',
      parentesco: 'Todos',
    }
    const [xxCurrentData, setXxCurrentData] = useState<IMovimentacaoResumed['result']>(xxAllDataz);
    const [xxCurrentFilters, setXxCurrentFilters] = useState(xxAllFilters);

    useEffect(() => {
      setXxAllData([...xxAllDataB]);
      setXxCurrentData([...xxAllDataB]);
    }, [xxAllDataB])
  
  
    const planoOptions = ['Todos', ...new Set(xxAllDataz.map(item => item.plano))];
    const parentescoOptions = ['Todos', ...new Set(xxAllDataz.map(item => item.parentesco))];
    const cargoOptions = ['Todos', ...new Set(xxAllDataz.map(item => item.cargo))];
  
    function handleFilters(filters: IFilters) {
      setXxCurrentFilters({ ...filters });
    }
  
  
    useEffect(() => {
      const filteredData = xxAllDataz.filter(data => {
        const shouldDisplayName = data.nome.toLocaleLowerCase().includes(xxCurrentFilters.nomeCpf.toLocaleLowerCase()) || data.cpf.includes(xxCurrentFilters.nomeCpf.replace(/[.-]/g, ''));
        const shouldDisplayPlano = xxCurrentFilters.plano === 'Todos' || xxCurrentFilters.plano === data.plano;
        const shouldDisplayCargo = xxCurrentFilters.cargo === 'Todos' || xxCurrentFilters.cargo === data.cargo;
        const shouldDisplayParentesco = xxCurrentFilters.parentesco === 'Todos' || xxCurrentFilters.parentesco === data.parentesco;
  
        return shouldDisplayName && shouldDisplayPlano && shouldDisplayCargo && shouldDisplayParentesco;
      });
      setXxCurrentData([ ...filteredData ]);
    }, [xxCurrentFilters, xxAllDataz]);

  const columns: GridColDef[] = [
    {
      field: 'nome',
      headerName: 'Nome',
      width: 200,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'cpf',
      headerName: 'CPF do titular',
      width: 150,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {Number(params.value) ? cpfMask(params.value) : params.value}
        </div>
      ),
    },
    {
      field: 'cpfDependente',
      headerName: 'CPF do dependente',
      width: 150,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {Number(params.value) ? cpfMask(params.value) : params.value}
        </div>
      ),
    },
    {
      field: 'dataAdmissao',
      headerName: 'Data Admissão',
      width: 130,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {!params.value ? "-" : formatDate(params.value)}
        </div>
      ),
    },
    {
      field: 'dataDemissao',
      headerName: 'Data Demissão',
      width: 150,
      renderCell: (params) => {
        if (!params.value) return <Typography variant="body2">-</Typography>;
        const isUsed = params.row.tipoDataExclusao === "DISMISSAL_DATE";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">
              {formatDate(params.value)}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: "primary.main" }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'dataTerminoAvisoPrevio',
      headerName: 'Término Aviso Prévio',
      width: 180,
      renderCell: (params) => {
        if (!params.value) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        const isUsed = params.row.tipoDataExclusao === "PRIOR_NOTICE_END_DATE";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">
              {formatDate(params.value)}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'movimentacao',
      headerName: 'Tipo de movimentação',
      width: 180,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value === "Demissão" ? "Exclusão" : params.value}
        </div>
      ),
    },
    {
      field: 'parentesco',
      headerName: 'Tipo de beneficiário',
      width: 160,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'registrationNumber',
      headerName: 'Número de matrícula',
      width: 160,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'plano',
      headerName: 'Plano',
      width: 120,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'personAdherenceStartDate',
      headerName: 'Data inicio',
      width: 120,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value === '-' ? params.value : formatDate(params.value)}
        </div>
      ),
    },
    {
      field: 'personExceptionExpireDate',
      headerName: 'Data exclusão',
      width: 130,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value === '-' ? params.value : formatDate(params.value)}
        </div>
      ),
    },
    {
      field: 'cargo',
      headerName: 'Cargo',
      width: 120,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'municipio',
      headerName: 'Municipio',
      width: 130,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </div>
      ),
    },
  ];

  const rowsWithId = xxCurrentData.map((row, index) => ({
    id: index,
    ...row,
  }));
  
    return (
      <>
        {/* <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <TextField
            fullWidth
            label='Nome beneficiário ou CPF'
            placeholder='Digite para pesquisar'
            variant='outlined'
            value={xxCurrentFilters.nomeCpf}
            onChange={(e) => handleFilters({ ...xxCurrentFilters, nomeCpf: e.target.value  })}
            InputProps={{ 
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon color='action' style={{ width: '24px', aspectRatio: '1' }}/>
                </InputAdornment>
              )
            }}
          />
          <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center', marginBottom: '24px' }}>
            <Typography color='gray' variant='body2'>Filtrar por</Typography>
            <FormControl style={{ width: '165px' }}>
              <InputLabel id="plano">Plano</InputLabel>
              <Select
                labelId="plano"
                label="Plano"
                value={xxCurrentFilters.plano}
                defaultValue={'Todos'}
                //@ts-ignore
                onChange={(e) => handleFilters({ ...xxCurrentFilters, plano: e.target.value  })}
                style={{ height: '40px' }}
              >
                {planoOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl style={{ width: '165px' }}>
              <InputLabel id="parentesco">Parentesco</InputLabel>
              <Select
                labelId="parentesco"
                label="Parentesco"
                value={xxCurrentFilters.parentesco}
                defaultValue={'Todos'}
                //@ts-ignore
                onChange={(e) => handleFilters({ ...xxCurrentFilters, parentesco: e.target.value  })}
                style={{ height: '40px' }}
              >
                {parentescoOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl style={{ width: '165px' }}>
              <InputLabel id="cargo">Cargo</InputLabel>
              <Select
                labelId="cargo"
                label="Cargo"
                value={xxCurrentFilters.cargo}
                defaultValue={'Todos'}
                //@ts-ignore
                onChange={(e) => handleFilters({ ...xxCurrentFilters, cargo: e.target.value  })}
                style={{ height: '40px' }}
              >
                {cargoOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button onClick={() => handleFilters({ nomeCpf: '', cargo: 'Todos', parentesco: 'Todos', plano: 'Todos' })}>Limpar filtro</Button>
          </div>
        </div> */}
        <div style={{ height: 700, width: '100%' }}>
          <DataGrid
            rows={rowsWithId}
            columns={columns}
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            initialState={{
              sorting: {
                sortModel: [{ field: 'nome', sort: 'asc' }],
              },
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                backgroundColor: '#FFFFFF !important',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#FFFFFF !important',
              },
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-virtualScroller': {
                '&::-webkit-scrollbar': {
                  width: '12px',
                  height: '12px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#C1C1C1',
                  borderRadius: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#F5F5F5',
                },
              },
            }}
          />
        </div>
      </>
    );
}
